/**
 * Job-card quality chip helpers.
 *
 * `UserJob.output_quality` is a compact JSON string the generator writes just
 * before a job flips to Ready (see `generation/final_files.py` —
 * `build_output_quality_payload` / `output_quality_json`). It grades the resume
 * that was actually RENDERED for that job, so it is deliberately absent on
 * every job generated before the field existed and on any job whose scoring
 * step failed — both cases render no chip at all rather than a fake score.
 *
 * Everything here is defensive: the field is free-form JSON from another
 * system, so a malformed or partial payload must degrade to "no chip", never
 * to a thrown render.
 */
import type { OutputQuality, JobPhase, RecruiterGrade, RecruiterCheckSummary } from '../types';

/** Phases where a generated resume exists, so a quality chip is meaningful. */
export const QUALITY_CHIP_PHASES: JobPhase[] = [
  'Ready', 'Applied', 'Follow-Up', 'Interviewing',
];

const BANDS = ['high', 'med', 'low'] as const;
const GRADES = ['A', 'B', 'C', 'D'] as const;

/** Parse the stored JSON string. Returns null for absent/unparseable/incomplete. */
export function parseOutputQuality(raw?: string | null): OutputQuality | null {
  if (!raw || typeof raw !== 'string') return null;
  let data: any;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  if (typeof data.score !== 'number' || !Number.isFinite(data.score)) return null;
  if (!BANDS.includes(data.band)) return null;
  const counts = data.counts && typeof data.counts === 'object' ? data.counts : {};
  // Recruiter Check fields — every one optional. A payload WITHOUT `grade` is a
  // legacy render and must keep its High/Med/Low presentation untouched.
  const grade: RecruiterGrade | undefined =
    GRADES.includes(data.grade) ? data.grade : undefined;
  let rc: RecruiterCheckSummary | undefined;
  if (data.rc && typeof data.rc === 'object' && !Array.isArray(data.rc)) {
    const rawCounts = data.rc.counts && typeof data.rc.counts === 'object' ? data.rc.counts : {};
    const cleanCounts: Record<string, number> = {};
    Object.keys(rawCounts).forEach((k) => {
      const n = Number((rawCounts as Record<string, unknown>)[k]);
      if (Number.isFinite(n)) cleanCounts[k] = n;
    });
    rc = {
      counts: cleanCounts,
      caps: Array.isArray(data.rc.caps)
        ? data.rc.caps.filter((c: unknown) => typeof c === 'string' && c.trim())
        : [],
      v: typeof data.rc.v === 'number' ? data.rc.v : 1,
    };
  }
  return {
    v: typeof data.v === 'number' ? data.v : 1,
    score: Math.max(0, Math.min(100, Math.round(data.score))),
    band: data.band,
    counts: {
      high: Number(counts.high) || 0,
      med: Number(counts.med) || 0,
      low: Number(counts.low) || 0,
      fab: Number(counts.fab) || 0,
    },
    top: Array.isArray(data.top) ? data.top.filter((t: any) => typeof t === 'string' && t.trim()) : [],
    citations_missing: Array.isArray(data.citations_missing)
      ? data.citations_missing.filter((c: any) => typeof c === 'string' && c.trim())
      : [],
    lint_version: typeof data.lint_version === 'string' ? data.lint_version : undefined,
    at: typeof data.at === 'string' ? data.at : undefined,
    grade,
    verdict: typeof data.verdict === 'string' && data.verdict.trim() ? data.verdict : undefined,
    rc,
    impression:
      typeof data.impression === 'string' && data.impression.trim() ? data.impression : undefined,
  };
}

// ── Recruiter Grade (BR-5) ─────────────────────────────────────────────────
//
// When the payload carries a `grade`, the chip stops being a High/Med/Low
// quality band and becomes the Recruiter Grade: a letter plus the one word a
// recruiter's reaction amounts to. Tough on purpose — C reads "Risky", D reads
// "Likely pass" — and never inflated client-side (the server owns the caps).

/** True when this payload was scored by Recruiter Check. */
export function hasRecruiterGrade(q: OutputQuality | null | undefined): boolean {
  return !!q && !!q.grade;
}

const GRADE_WORDS: Record<RecruiterGrade, string> = {
  A: 'Strong',
  B: 'Good',
  C: 'Risky',
  D: 'Likely pass',
};

/** Chip label for a graded payload, e.g. "A · Strong". */
export function gradeLabel(grade: RecruiterGrade): string {
  return `${grade} · ${GRADE_WORDS[grade]}`;
}

/** The one-word recruiter reaction behind a grade. */
export function gradeWord(grade: RecruiterGrade): string {
  return GRADE_WORDS[grade];
}

/** Chip classes — same pill vocabulary as the legacy band chip. */
export function gradeChipClasses(grade: RecruiterGrade): string {
  if (grade === 'A') return 'bg-green-100 text-green-800';
  if (grade === 'B') return 'bg-teal-100 text-teal-800';
  if (grade === 'C') return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-800';
}

/** Bordered variant for the lightbox's verdict banner. */
export function gradeBannerClasses(grade: RecruiterGrade): string {
  if (grade === 'A') return 'border-green-200 bg-green-50 text-green-900';
  if (grade === 'B') return 'border-teal-200 bg-teal-50 text-teal-900';
  if (grade === 'C') return 'border-amber-200 bg-amber-50 text-amber-900';
  return 'border-red-200 bg-red-50 text-red-900';
}

/**
 * Grades a recruiter is likely to pass on. Drives the apply-time confirm
 * (BR-7) — no grade at all (legacy card) is deliberately NOT risky, so old
 * cards get zero new friction.
 */
export function isRiskyGrade(grade?: RecruiterGrade | null): boolean {
  return grade === 'C' || grade === 'D';
}

/** Chip label — plain language, never "AI score". */
export function qualityLabel(q: OutputQuality): string {
  const word = q.band === 'high' ? 'High' : q.band === 'med' ? 'Med' : 'Low';
  return `Quality: ${word}`;
}

/** Tailwind classes matching the existing pill/chip vocabulary on the card. */
export function qualityChipClasses(band: OutputQuality['band']): string {
  if (band === 'high') return 'bg-green-100 text-green-800';
  if (band === 'med') return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-800';
}

/** The missing-citation sentence, or null when every citation still holds. */
export function citationsSentence(q: OutputQuality): string | null {
  if (!q.citations_missing.length) return null;
  return `A highlight credits ${q.citations_missing.join(', ')}, but this version of the resume no longer shows that work.`;
}

/** True when there is nothing at all to show beyond the score. */
export function qualityIsClean(q: OutputQuality): boolean {
  return !q.top.length && !q.citations_missing.length;
}

const COUNT_LABELS: { key: keyof OutputQuality['counts']; label: string; classes: string }[] = [
  { key: 'high', label: 'High', classes: 'bg-red-50 text-red-700 border-red-200' },
  { key: 'med', label: 'Medium', classes: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'low', label: 'Low', classes: 'bg-gray-50 text-gray-600 border-gray-200' },
  { key: 'fab', label: 'Unsupported figures', classes: 'bg-purple-50 text-purple-700 border-purple-200' },
];

/** The counts row, as labeled chips. Always all four, so a zero reads as "clear". */
export function qualityCountChips(q: OutputQuality) {
  return COUNT_LABELS.map(({ key, label, classes }) => ({
    key,
    label,
    count: q.counts[key],
    classes,
  }));
}

/** "Checked <local date>", or null when the payload carries no timestamp. */
export function qualityCheckedAt(q: OutputQuality): string | null {
  if (!q.at) return null;
  const d = new Date(q.at);
  if (Number.isNaN(d.getTime())) return null;
  return `Checked ${d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  })}`;
}

/**
 * Plain-text rendering of the same content the quality window shows. The card
 * opens the window on click rather than showing this, but the string is the
 * canonical copy check — coaching, never "AI score", never "errors".
 */
export function qualityTooltip(q: OutputQuality): string {
  const lines: string[] = [`Generated resume quality: ${q.score}/100`];
  if (q.top.length) {
    lines.push('', 'Worth a look:');
    q.top.forEach((t) => lines.push(`• ${t}`));
  }
  const cites = citationsSentence(q);
  if (cites) lines.push('', cites);
  if (qualityIsClean(q)) lines.push('', 'Nothing flagged on this one.');
  return lines.join('\n');
}

/**
 * The chip's data, or null when no chip should render.
 *
 * A payload scored by Recruiter Check shows the LETTER GRADE; anything older
 * keeps the original "Quality: High/Med/Low" chip, so no existing card changes
 * appearance until it is re-rendered.
 */
export function qualityChip(
  phase: JobPhase,
  raw?: string | null,
): {
  label: string;
  classes: string;
  tooltip: string;
  quality: OutputQuality;
  grade?: RecruiterGrade;
} | null {
  if (!QUALITY_CHIP_PHASES.includes(phase)) return null;
  const q = parseOutputQuality(raw);
  if (!q) return null;
  if (q.grade) {
    return {
      label: gradeLabel(q.grade),
      classes: gradeChipClasses(q.grade),
      tooltip: recruiterTooltip(q),
      quality: q,
      grade: q.grade,
    };
  }
  return {
    label: qualityLabel(q),
    classes: qualityChipClasses(q.band),
    tooltip: qualityTooltip(q),
    quality: q,
  };
}

/** Plain-text summary of a GRADED payload (the chip's accessible description). */
export function recruiterTooltip(q: OutputQuality): string {
  const lines: string[] = [];
  if (q.grade) lines.push(`Recruiter grade ${gradeLabel(q.grade)}`);
  if (q.verdict) lines.push(q.verdict);
  if (q.impression) lines.push('', q.impression);
  return lines.join('\n');
}
